import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { enhanceUseCase } from '../../../services/api';

const StepUseCase = ({ formData, updateFormData, errors, workspaceId, projectId, projectName, availableProviders }) => {
  const navigate = useNavigate();
  const [isEnhancing, setIsEnhancing] = useState(false);
  const charCount = formData.use_case.length;
  const isValid = charCount >= 10;
  const isGroqAvailable = !availableProviders || availableProviders.groq !== false;

  const handleEnhance = async () => {
    if (!formData.use_case || formData.use_case.trim().length < 10) {
      toast.warning('Please enter at least 10 characters before enhancing');
      return;
    }

    setIsEnhancing(true);
    try {
      const result = await enhanceUseCase(formData.use_case, formData.description, workspaceId, projectId, projectName);
      if (result.success) {
        updateFormData('use_case', result.data);
        toast.success('Use case enhanced successfully!');
      } else {
        toast.error(result.error || 'Failed to enhance use case');
      }
    } catch (error) {
      toast.error('Failed to enhance use case');
    } finally {
      setIsEnhancing(false);
    }
  };

  return (
    <div className="wizard-step">
      <div className="step-intro">
        <h3>What's your primary use case?</h3>
        <p>Describe the context where this prompt will be used.</p>
      </div>

      <div className="form-group">
        <div className="form-label-row">
          <label htmlFor="use_case" className="form-label">
            Describe your use case
            <span className="label-required">*</span>
          </label>
          <div className="flex items-center gap-2">
            {!isGroqAvailable && (
              <button
                type="button"
                onClick={() => navigate('/workspace-settings', { state: { tab: 'apikeys' } })}
                className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded transition-all duration-fast bg-error/10 hover:bg-error/20 text-error border border-error/30 hover:border-error/50"
                title="Configure Groq API key"
              >
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z"/>
                </svg>
                Configure
              </button>
            )}
            <div className="relative">
              <button
                type="button"
                onClick={handleEnhance}
                disabled={isEnhancing || charCount < 10 || !isGroqAvailable}
                className="btn-enhance"
                title={!isGroqAvailable ? 'Groq API key not configured' : 'Enhance using AI'}
              >
                {isEnhancing ? 'Enhancing...' : 'Enhance'}
              </button>
              {!isGroqAvailable && (
                <span 
                  className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-error text-white text-[8px] flex items-center justify-center font-bold border border-bg-secondary" 
                  title="Groq API key not configured"
                >
                  !
                </span>
              )}
            </div>
          </div>
        </div>
        <textarea
          id="use_case"
          value={formData.use_case}
          onChange={(e) => updateFormData('use_case', e.target.value)}
          placeholder="e.g., Customer support team handling complaint tickets in an e-commerce platform..."
          className={`form-textarea ${errors?.use_case ? 'form-error' : ''}`}
          rows="5"
          autoFocus
          disabled={isEnhancing}
        />
        <div className="form-footer">
          <p className={`form-hint ${errors?.use_case ? 'error-text' : ''}`}>
            {errors?.use_case || 'Minimum 10 characters required'}
          </p>
          <span className={`char-count ${isValid ? 'valid' : ''}`}>
            {charCount} / 10+
          </span>
        </div>
      </div>
    </div>
  );
};

StepUseCase.propTypes = {
  formData: PropTypes.shape({
    use_case: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  updateFormData: PropTypes.func.isRequired,
  errors: PropTypes.object,
  workspaceId: PropTypes.string,
  projectId: PropTypes.string,
  projectName: PropTypes.string,
  availableProviders: PropTypes.shape({
    openai: PropTypes.bool,
    anthropic: PropTypes.bool,
    groq: PropTypes.bool,
  }),
};

StepUseCase.defaultProps = {
  errors: {},
  workspaceId: null,
  projectId: null,
  projectName: null,
  availableProviders: null,
};

export default StepUseCase;
